/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Tonality } from '@dsny/dsny-component-library';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CUSTOMER_SUPPORT_EMAIL } from 'src/utils/Urls';
import { useSelector } from 'react-redux';
import { RootState } from 'src/app/store';
import { validateSpinsDetection } from 'src/utils/VerifyTrackStatus';
import {
  Genre,
  Momentum,
  Peakairplay,
  Region,
  Rotation,
} from '../../../assets/customIcons';
import {
  ArtistNameStyle,
  MetricWrapper,
  SongTitleStyle,
  TotalSpinsStyle,
  SomethingWrongWrapper,
  ContactLink,
  NoResultFoundWrapper,
  BulkSelectionWrapperStyles,
  SingleSelectionWrapperStyles,
  TableFlexWrapper,
  TableMessagesWrapper,
  TableContainer,
  InsightsWrapper,
  IconWrapper,
} from './SongTable.styles';
import { SongLibrary } from '../Library.slice';
import { useSorting } from './useSorting';
import ContextDropdown from '../ContextDropdown/ContextDropdown';
import { SortingRule, TableSongData } from '../Library';

interface SongTableDesktopProps {
  songsLibrary: SongLibrary[];
  rowSelection: { [key: number]: boolean };
  tableData: TableSongData[];
  sortedTableData: TableSongData[];
  isFetching: boolean;
  getComparisonBadge: (spinsDifference: number) => React.ReactNode;
  getTrackStatus: (
    startTrackingOn: string,
    endTrackingOn: string,
    songId: string
  ) => React.ReactNode;
  handleNavigate: (songId: string) => void;
  setRowSelection: (
    selection:
      | Record<number, boolean>
      | ((selection: Record<number, boolean>) => Record<number, boolean>)
  ) => void;
  exportCSV: (songIds: string[]) => void;
  handleSorting: (sorting: SortingRule[], songSortSelected: boolean) => void;
}

// Table row type definition
type TableRow = {
  songId: string;
  song: string;
  artist: string;
  spins_all_count: number;
  spins_count: number;
  spins_difference: number;
  status: number;
  startTrackingOn: string;
  endTrackingOn: string;
  insightsFlags?: SongLibrary['insightsFlags'];
  topGenres?: string[];
  topRegions?: string[];
};

const TABLE_SKELETON_ROW_COUNT = 7;

// Table component - desktop
const SongTableDesktop: React.FC<SongTableDesktopProps> = ({
  rowSelection,
  tableData,
  sortedTableData,
  isFetching,
  getComparisonBadge,
  getTrackStatus,
  handleNavigate,
  setRowSelection,
  exportCSV,
  handleSorting,
}) => {
  const { t } = useTranslation();

  const { tableStatus } = useSelector((state: RootState) => state.library);

  // Sorting hook
  const { sorting, onSortingChange } = useSorting(
    tableStatus.sortingId,
    tableStatus.sortingOrder
  );

  useEffect(() => {
    handleSorting(sorting, false);
  }, [sorting, tableData]);

  // Momentum tooltips
  const momentumUpTooltip = t('LIBRARY_TABLE_INSIGHT_MOMENTUM_UP_TOOLTIP');
  const momentumDownTooltip = t('LIBRARY_TABLE_INSIGHT_MOMENTUM_DOWN_TOOLTIP');
  const momentumStagnantTooltip = t(
    'LIBRARY_TABLE_INSIGHT_MOMENTUM_STAGNANT_TOOLTIP'
  );
  // Peakairplay tooltip
  const peakairplayTooltip = t('LIBRARY_TABLE_INSIGHT_PEAKAIRPLAY_TOOLTIP');
  // Rotation tooltip
  const rotationTooltip = t('LIBRARY_TABLE_INSIGHT_ROTATION_TOOLTIP');

  // Region tooltip
  const getRegionTooltip = (regions: string[] = []) => {
    const [region1, region2, region3] = [...regions, '', '', '']
      .filter(Boolean)
      .slice(0, 3);
    const count = [region1, region2, region3].filter(Boolean).length;
    const key =
      count <= 1
        ? 'LIBRARY_TABLE_INSIGHT_REGION_TOOLTIP_ONE'
        : count === 2
        ? 'LIBRARY_TABLE_INSIGHT_REGION_TOOLTIP_TWO'
        : 'LIBRARY_TABLE_INSIGHT_REGION_TOOLTIP_THREE';
    return t(key, {
      region1: region1 || 'Region 1',
      region2: region2 || 'Region 2',
      region3: region3 || 'Region 3',
    });
  };

  // Genre tooltip
  const getGenreTooltip = (genres: string[] = []) => {
    const [genre1, genre2, genre3] = [...genres, '', '', '']
      .filter(Boolean)
      .slice(0, 3);
    const count = [genre1, genre2, genre3].filter(Boolean).length;
    const key =
      count <= 1
        ? 'LIBRARY_TABLE_INSIGHT_GENRE_TOOLTIP_ONE'
        : count === 2
        ? 'LIBRARY_TABLE_INSIGHT_GENRE_TOOLTIP_TWO'
        : 'LIBRARY_TABLE_INSIGHT_GENRE_TOOLTIP_THREE';
    return t(key, {
      genre1: genre1 || 'Genre 1',
      genre2: genre2 || 'Genre 2',
      genre3: genre3 || 'Genre 3',
    });
  };

  // Table columns definition
  const cols: Tonality.ColumnDef<TableRow>[] = [
    {
      id: 'select-col',
      enableSorting: false,
      maxSize: 50,
      isSelectionColumn: true,
      header: (state: Tonality.HeaderRender, onChange) => {
        const { someRowsAreSelected, allRowsAreSelected } = state;

        return (
          // Bulk selection
          <Tonality.Checkbox
            checked={someRowsAreSelected ? 'mixed' : allRowsAreSelected}
            onChange={onChange() as never}
            wrapperStyle={{ ...BulkSelectionWrapperStyles }}
          />
        );
      },
      render: ({ row }) => (
        // Single selection
        <Tonality.Checkbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
          wrapperStyle={{ ...SingleSelectionWrapperStyles }}
        />
      ),
    },
    {
      id: 'song',
      header: t('LIBRARY_TABLE_HEADER_SONG_TITLE'),
      accessorKey: 'song',
      enableSorting: true,
      enableResizing: true,
      size: 280,
      minSize: 200,
      maxSize: 320,
      render: (cell, row) => (
        <SongTitleStyle onClick={() => handleNavigate(row.original.songId)}>
          {row.original.song}
        </SongTitleStyle>
      ),
    },
    {
      id: 'artist',
      header: t('LIBRARY_TABLE_HEADER_ARTIST_NAME'),
      accessorKey: 'artist',
      enableSorting: true,
      enableResizing: true,
      maxSize: 100,
      render: (cell, row) => (
        <ArtistNameStyle onClick={() => handleNavigate(row.original.songId)}>
          {row.original.artist}
        </ArtistNameStyle>
      ),
    },
    {
      id: 'spins',
      header: t('LIBRARY_TABLE_HEADER_SPINS'),
      accessorKey: 'spins',
      enableSorting: true,
      enableResizing: true,
      maxSize: 100,
      tooltip: t('LIBRARY_TABLE_HEADER_SPINS_TOOLTIP'),
      render: (cell, row) => (
        <MetricWrapper onClick={() => handleNavigate(row.original.songId)}>
          <TotalSpinsStyle>{row.original.spins_all_count}</TotalSpinsStyle>
          {getComparisonBadge(Number(row.original.spins_difference))}
        </MetricWrapper>
      ),
    },
    {
      id: 'insights',
      header: t('LIBRARY_TABLE_HEADER_INSIGHTS'),
      enableSorting: true,
      enableResizing: true,
      maxSize: 200,
      tooltip: t('LIBRARY_TABLE_HEADER_INSIGHTS_TOOLTIP'),
      render: (cell, row) => {
        const flags = row.original.insightsFlags;
        const hasMomentum =
          flags?.hasMomentumGainInsight ||
          flags?.hasMomentumStagnantInsight ||
          flags?.hasMomentumDownInsight;
        const hasPeak =
          flags?.hasPeakAirplayMorningInsight ||
          flags?.hasPeakAirplayEveningInsight ||
          flags?.hasPeakAirplayNightInsight;
        const hasRotation = flags?.hasRotationInsight;
        const hasRegion = flags?.hasRegionsInsight;
        const hasGenre = flags?.hasGenresInsight;
        const hasAny =
          hasMomentum || hasPeak || hasRotation || hasRegion || hasGenre;

        return (
          <InsightsWrapper onClick={() => handleNavigate(row.original.songId)}>
            {hasMomentum && (
              <Tonality.Tooltip
                content={
                  flags?.hasMomentumGainInsight
                    ? momentumUpTooltip
                    : flags?.hasMomentumDownInsight
                    ? momentumDownTooltip
                    : momentumStagnantTooltip
                }
                placement="top"
                hasArrow={false}
              >
                <IconWrapper>
                  <Momentum />
                </IconWrapper>
              </Tonality.Tooltip>
            )}

            {hasPeak && (
              <Tonality.Tooltip
                content={peakairplayTooltip}
                placement="top"
                hasArrow={false}
              >
                <IconWrapper>
                  <Peakairplay />
                </IconWrapper>
              </Tonality.Tooltip>
            )}

            {hasRotation && (
              <Tonality.Tooltip
                content={rotationTooltip}
                placement="top"
                hasArrow={false}
              >
                <IconWrapper>
                  <Rotation />
                </IconWrapper>
              </Tonality.Tooltip>
            )}

            {hasRegion && (
              <Tonality.Tooltip
                content={getRegionTooltip(row.original.topRegions)}
                placement="top"
                hasArrow={false}
              >
                <IconWrapper>
                  <Region />
                </IconWrapper>
              </Tonality.Tooltip>
            )}

            {hasGenre && (
              <Tonality.Tooltip
                content={getGenreTooltip(row.original.topGenres)}
                placement="top"
                hasArrow={false}
              >
                <IconWrapper>
                  <Genre />
                </IconWrapper>
              </Tonality.Tooltip>
            )}

            {!hasAny && t('LIBRARY_TABLE_INSIGHT_NO_ACTIVE')}
          </InsightsWrapper>
        );
      },
    },
    {
      id: 'status',
      header: t('LIBRARY_TABLE_HEADER_STATUS'),
      accessorKey: 'status',
      enableSorting: true,
      enableResizing: true,
      maxSize: 100,
      tooltip: t('LIBRARY_TABLE_HEADER_STATUS_TOOLTIP'),
      render: (cell, row) => (
        <>
          {getTrackStatus(
            row.original.startTrackingOn,
            row.original.endTrackingOn,
            row.original.songId
          )}
        </>
      ),
    },
    {
      id: 'context_menu',
      enableSorting: false,
      maxSize: 50,
      render: (cell, row) => (
        <>
          <ContextDropdown
            songId={row.original.songId}
            displayMenuOnTop={sortedTableData.length === row.index + 1}
            isExportAvailable={validateSpinsDetection(
              row.original.spins_all_count
            )}
            handleNavigate={handleNavigate}
            exportCSV={exportCSV}
          />
        </>
      ),
    },
  ];

  return (
    <TableFlexWrapper>
      <TableMessagesWrapper>
        {/* Something went wrong message */}
        {tableData.length === 0 && !isFetching && (
          <SomethingWrongWrapper>
            <span>{t('LIBRARY_TABLE_ERROR_MESSAGE')}</span>
            <ContactLink
              href={`mailto:${CUSTOMER_SUPPORT_EMAIL}`}
              target="_blank"
              rel="noreferrer"
            >
              {t('LIBRARY_TABLE_CONTACT_SUPPORT_LINK')}
            </ContactLink>
          </SomethingWrongWrapper>
        )}

        {/* No results found message */}
        {!sortedTableData.length && !isFetching && (
          <NoResultFoundWrapper>
            <span>{t('LIBRARY_TABLE_NO_RESULT_FOUND_MESSAGE')}</span>
          </NoResultFoundWrapper>
        )}
      </TableMessagesWrapper>
      <TableContainer>
        <Tonality.Table
          data={sortedTableData}
          cols={cols}
          mode="light"
          setting="mtr"
          rowSelection={rowSelection}
          showPagination={false}
          pagination={{
            pageIndex: 0,
            pageSize: TABLE_SKELETON_ROW_COUNT,
            rowCount: sortedTableData.length,
          }}
          sorting={sorting}
          onSortingChange={onSortingChange}
          onRowSelectionChange={setRowSelection}
          loading={isFetching}
        />
      </TableContainer>
    </TableFlexWrapper>
  );
};

export default SongTableDesktop;
